/** Isoloir : saisie du code, choix des items, dépôt du bulletin. */

import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { CODE_LENGTHS, isValidCode, normalizeCode, type Item } from '../../core/index.js';
import { StoreElement } from '../base.js';
import { pluriel } from '../format.js';
import { icone } from '../icons.js';
import { valeurDe } from '../md.js';

type Etape = 'code' | 'vote' | 'confirme';

@customElement('view-isoloir')
export class ViewIsoloir extends StoreElement {
  @state() private etape: Etape = 'code';
  @state() private code = '';
  @state() private choix: string[] = [];
  @state() private erreur = '';
  @state() private occupe = false;

  private get tour(): 1 | 2 {
    return this.store.state.phase === 'TOUR2' ? 2 : 1;
  }

  private get voixDisponibles(): number {
    return this.store.votersForRound(this.tour);
  }

  private itemsEnLice(): Item[] {
    const { computation } = this.store;
    if (this.tour === 1) return computation.items;
    return computation.items.filter((item) => computation.itemsEnLice2.includes(item.id));
  }

  private basculer(itemId: string): void {
    if (this.choix.includes(itemId)) {
      this.choix = this.choix.filter((id) => id !== itemId);
      return;
    }
    if (this.choix.length >= this.voixDisponibles) return;
    this.choix = [...this.choix, itemId];
  }

  private validerCode(): void {
    const propre = normalizeCode(this.code);
    if (!isValidCode(propre, CODE_LENGTHS.eleve)) {
      this.erreur = 'Ce code est incomplet ou mal recopié. Vérifiez les 9 caractères imprimés sur votre papier.';
      return;
    }
    if (!this.store.codeIsKnown(propre)) {
      this.erreur = 'Ce code n’appartient pas à cette élection. Adressez-vous à votre professeur.';
      return;
    }
    this.erreur = '';
    this.etape = 'vote';
  }

  private async deposer(blanc: boolean): Promise<void> {
    this.occupe = true;
    const resultat = await this.store.castBallot(this.code, blanc ? [] : this.choix, blanc, this.tour);
    this.occupe = false;
    if (resultat === 'ok') {
      this.etape = 'confirme';
      return;
    }
    if (resultat === 'double') {
      this.erreur = 'Ce code a déjà voté pour ce tour. Un bulletin est définitif.';
      this.etape = 'code';
      this.choix = [];
      return;
    }
    this.erreur = 'Code inconnu de cette élection.';
    this.etape = 'code';
    this.choix = [];
  }

  private suivant(): void {
    this.etape = 'code';
    this.code = '';
    this.choix = [];
    this.erreur = '';
  }

  override render() {
    const phase = this.store.state.phase;
    const scrutinOuvert = phase === 'TOUR1' || phase === 'TOUR2';

    return html`
      <div class="page page--narrow">
        <div class="row row--between">
          <div class="stack stack--tight">
            <span class="eyebrow">${icone('vote', 17)} Isoloir</span>
            <h1 class="t-headline">${this.store.state.config?.nom ?? 'Vote'}</h1>
          </div>
          <md-text-button @click=${() => (location.hash = '#/')}>Quitter</md-text-button>
        </div>

        ${!scrutinOuvert
          ? html`<section class="card card--warn">
              <div class="card__head">
                <span class="chip-icon chip-icon--alerte" aria-hidden="true">${icone('horloge', 22)}</span>
                <h2 class="t-title">Le vote n’est pas ouvert</h2>
              </div>
              <p class="t-body">L’enseignant n’a pas encore ouvert le scrutin, ou l’a déjà clos.</p>
            </section>`
          : nothing}
        ${this.erreur
          ? html`<section class="card card--warn">
              <div class="card__head">
                <span class="chip-icon chip-icon--alerte" aria-hidden="true">${icone('alerte', 22)}</span>
                <p class="t-body">${this.erreur}</p>
              </div>
            </section>`
          : nothing}
        ${scrutinOuvert ? this.renderEtape() : nothing}
      </div>
    `;
  }

  private renderEtape() {
    if (this.etape === 'code') return this.renderCode();
    if (this.etape === 'vote') return this.renderVote();
    return this.renderConfirmation();
  }

  private renderCode() {
    return html`
      <section class="card">
        <div class="card__head">
          <span class="card__step" aria-hidden="true">1</span>
          <h2 class="t-title">Votre code personnel</h2>
        </div>
        <p class="t-body t-muted">
          Il figure sur le papier remis par votre professeur. Le code est à usage unique et n’est jamais conservé en
          clair : seul votre vote est enregistré, jamais votre nom.
        </p>
        <input
          class="code-input"
          type="text"
          inputmode="latin"
          autocapitalize="characters"
          autocomplete="off"
          spellcheck="false"
          aria-label="Code personnel"
          placeholder="XXXXX-XXXX"
          .value=${this.code}
          @input=${(evenement: Event) => {
            this.code = valeurDe(evenement);
            this.erreur = '';
          }}
          @keydown=${(evenement: KeyboardEvent) => {
            if (evenement.key === 'Enter') this.validerCode();
          }}
        />
        <div class="row row--end">
          <md-filled-button class="btn--big" ?disabled=${this.code.trim().length === 0} @click=${() => this.validerCode()}
            >Continuer</md-filled-button
          >
        </div>
      </section>
    `;
  }

  private renderVote() {
    const items = this.itemsEnLice();
    const tour = this.tour;
    const complet = this.choix.length >= this.voixDisponibles;

    return html`
      <section class="card">
        <div class="card__head">
          <span class="card__step" aria-hidden="true">2</span>
          <h2 class="t-title">
            ${tour === 1 ? 'Premier tour' : 'Second tour'} — choisissez
            ${this.voixDisponibles === 1 ? 'un nom' : 'deux noms'}
          </h2>
        </div>

        <div class="row row--between">
          <span class="t-body t-muted">
            ${pluriel(this.voixDisponibles, 'voix disponible', 'voix disponibles')}
          </span>
          <span class="progress-dots" aria-hidden="true">
            ${Array.from(
              { length: this.voixDisponibles },
              (_, index) =>
                html`<span class="progress-dots__point ${index < this.choix.length ? 'progress-dots__point--plein' : ''}"
                ></span>`,
            )}
          </span>
        </div>
        <p class="t-body t-muted">
          Vous pouvez voter pour un binôme — un candidat et son suppléant — ou pour une personne seule. Un bulletin
          portant une seule voix reste valide.
        </p>

        <div class="ballot" role="group" aria-label="Choix du bulletin">
          ${items.map((item) => {
            const choisi = this.choix.includes(item.id);
            return html`
              <button
                type="button"
                class="option ${choisi ? 'option--picked' : ''}"
                aria-pressed=${choisi ? 'true' : 'false'}
                ?disabled=${!choisi && complet}
                @click=${() => this.basculer(item.id)}
              >
                <span class="option__mark" aria-hidden="true">${choisi ? icone('check', 18) : nothing}</span>
                <span class="option__corps grow">
                  <span class="t-title">${item.label}</span>
                  <span class="option__kind">
                    ${item.kind === 'binome' ? 'Binôme — candidat et suppléant' : 'Candidature individuelle — sans suppléant'}
                  </span>
                </span>
              </button>
            `;
          })}
        </div>

        ${items.length === 0
          ? html`<p class="t-body t-error">Aucun nom n’est proposé : prévenez votre professeur.</p>`
          : nothing}

        <div class="row row--between actions-fixes">
          <md-outlined-button ?disabled=${this.occupe} @click=${() => void this.deposer(true)}>Vote blanc</md-outlined-button>
          <md-filled-button
            class="btn--big"
            ?disabled=${this.occupe || (!complet && this.choix.length === 0)}
            @click=${() => void this.deposer(false)}
            >Déposer mon bulletin</md-filled-button
          >
        </div>
        <p class="t-body t-muted">Le dépôt est définitif : aucun vote ne peut être modifié ensuite.</p>
      </section>
    `;
  }

  private renderConfirmation() {
    return html`
      <section class="card card--ok">
        <div class="card__head">
          <span class="chip-icon chip-icon--ok" aria-hidden="true">${icone('checkCercle', 24)}</span>
          <h2 class="t-title">Bulletin déposé</h2>
        </div>
        <p class="t-body">
          Votre vote est enregistré. Il reste secret : personne, pas même l’enseignant, ne peut savoir ce que vous avez
          choisi.
        </p>
        <div class="row row--end">
          <md-filled-button class="btn--big" @click=${() => this.suivant()}>Élève suivant</md-filled-button>
        </div>
      </section>
    `;
  }
}
